<template>
  <div v-if="showRemediationBanner">
    <div class="d-flex align-center mb-3">
      <v-icon :icon="icon" size="24" class="mr-2" />
      <div class="subtitle1">
        {{ title }}
      </div>
    </div>
    <div v-if="showQuickFixSection">
      <div class="d-flex align-center mb-2" v-for="(item, index) in quickFixSteps" :key="index">
        <div class="quick-fix-number mr-2">
          {{ index + 1 }}
        </div>
        <div class="body2" v-html="$t(`ticketDetails.remediationSection.quickFix.${item}`)" />
      </div>
    </div>
    <div v-if="showRecommendations">
      <div class="d-flex align-center mb-2">
        <div class="body2" data-testid="ticket-recommendations" v-html="$t(recommendationsKey)" />
      </div>
    </div>
    <v-alert
      v-if="showSecondaryBanner"
      icon="icon-warning-xs"
      color="indigo-faint"
      class="mb-5 mt-5 text-primary"
    >
      <div
        class="body2"
        v-html="
          $t(`ticketDetails.remediationSection.secondaryBanner.${ticketDetails.eventTrigger}`)
        "
      />
    </v-alert>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType, computed } from "vue";
import type { TicketDetails } from "@/_store/tickets/tickets.module.ts";
import { quickFixStepsMap } from "@/constants/tickets.ts";
import { useI18n } from "vue-i18n";
import { ticketQuickFixSectionExists } from "@/_helpers/ticket-helpers.ts";

export default defineComponent({
  props: {
    ticketDetails: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
  },
  setup(props) {
    const i18n = useI18n();

    const showQuickFixSection = computed(() => {
      return ticketQuickFixSectionExists(props.ticketDetails);
    });

    const title = computed(() => {
      return showQuickFixSection.value
        ? i18n.t("ticketDetails.remediationSection.quickFixTitle")
        : i18n.t("ticketDetails.remediationSection.recommendationTitle");
    });

    const icon = computed(() => {
      return showQuickFixSection.value ? "icon-insights-xs" : "icon-protection-xs";
    });

    const quickFixSteps = computed(() => {
      return quickFixStepsMap[props.ticketDetails.eventTrigger];
    });

    const showRemediationBanner = computed(() => {
      return (
        !props.ticketDetails.processed &&
        (showQuickFixSection.value || showRecommendations.value || showSecondaryBanner.value)
      );
    });

    const showSecondaryBanner = computed(() => {
      return !!i18n.te(
        `ticketDetails.remediationSection.secondaryBanner.${props.ticketDetails.eventTrigger}`
      );
    });

    const recommendationsKey = computed(() => {
      const detectionReason =
        props.ticketDetails.sections?.edrMetaData?.asrDetails?.detectionReason;
      const reasonKey = `ticketDetails.remediationSection.recommendations.${detectionReason}`;

      return detectionReason && i18n.te(reasonKey)
        ? reasonKey
        : `ticketDetails.remediationSection.recommendations.${props.ticketDetails.eventTrigger}`;
    });

    const showRecommendations = computed(() => {
      return !!i18n.te(recommendationsKey.value);
    });

    return {
      title,
      icon,
      quickFixStepsMap,
      showQuickFixSection,
      showRecommendations,
      recommendationsKey,
      showSecondaryBanner,
      quickFixSteps,
      showRemediationBanner,
    };
  },
});
</script>

<style scoped lang="scss">
.quick-fix-number {
  min-height: 24px;
  min-width: 24px;
  height: 24px;
  width: 24px;
  border-radius: 12px;
  background-color: rgb(var(--v-theme-indigo-faint));
  text-align: center;
  color: rgb(var(--v-theme-primary));
  font-size: 14px;
  line-height: 24px;
  font-style: normal;
  font-weight: 600;
}

.v-alert {
  padding: 8px !important;
  border-radius: 8px !important;
}

.v-alert__prepend {
  margin-right: 5px !important;
}

:deep(*) {
  .v-alert__prepend {
    margin-right: 5px !important;
  }
}
</style>
